import React, { createContext, useState, useContext, useCallback } from 'react';
import { fetchAvailableVehicles, fetchAvailableVehiclesByDateRange } from '../services/vehicleService';

const VehicleContext = createContext();

export const VehicleProvider = ({ children }) => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize selectedDates from sessionStorage if available
  const getInitialDates = () => {
    try {
      const savedDates = sessionStorage.getItem('selectedDates');
      return savedDates ? JSON.parse(savedDates) : { pickupDate: '', dropoffDate: '' };
    } catch (error) {
      console.error('Error reading dates from sessionStorage:', error);
      return { pickupDate: '', dropoffDate: '' };
    }
  };

  const [selectedDates, setSelectedDatesState] = useState(getInitialDates());

  // Wrapper to save to sessionStorage whenever dates change
  const setSelectedDates = (dates) => {
    try {
      const newDates = typeof dates === 'function' ? dates(selectedDates) : dates;
      sessionStorage.setItem('selectedDates', JSON.stringify(newDates));
      setSelectedDatesState(newDates);
    } catch (error) {
      console.error('Error saving dates to sessionStorage:', error);
      setSelectedDatesState(dates);
    }
  };

  const loadVehicles = useCallback(async (force = false, startDate = null, endDate = null) => {
    if (!force && loading) return; // Prevent concurrent requests
    setLoading(true);
    setError(null);
    try {
      let data;
      if (startDate && endDate) {
        data = await fetchAvailableVehiclesByDateRange(startDate, endDate);
      } else {
        data = await fetchAvailableVehicles();
      }
      setVehicles(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  const refreshVehicles = useCallback(async () => {
    await loadVehicles(true);
  }, [loadVehicles]);

  const searchVehiclesByDates = useCallback(async (pickupDate, dropoffDate) => {
    if (loading) return; // Prevent concurrent requests
    await loadVehicles(true, pickupDate, dropoffDate);
  }, [loadVehicles, loading]);

  // Only load vehicles once when provider mounts, but not on campervan detail pages
  React.useEffect(() => {
    const currentPath = window.location.pathname;
    const isCampervanDetailPage = currentPath.includes('/campervan/') && (currentPath.includes('sun-catchers') || currentPath.includes('wave-catchers') || /\/campervan\/\d+/.test(currentPath));
    
    if (!isCampervanDetailPage) {
      loadVehicles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <VehicleContext.Provider value={{
      vehicles,
      loading,
      error,
      loadVehicles,
      refreshVehicles,
      searchVehiclesByDates,
      setVehicles,
      selectedDates,
      setSelectedDates
    }}>

      {children}
    </VehicleContext.Provider>
  );
};

export const useVehicles = () => {
  const context = useContext(VehicleContext);
  if (!context) {
    throw new Error('useVehicles must be used within a VehicleProvider');
  }
  return context;
};
